import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { useAchievementRewards, useGameVictoryRewards } from '@/hooks/use-currency';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/use-toast';
import { Trophy, Zap, Medal, Coins, Gem, AlertCircle } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import appSound from '@/services/app-sound-service';

export interface Achievement {
  id: number;
  name: string;
  description: string;
  category: string;
  requirement: number;
  imageUrl: string;
  progress: number;
  completed: boolean;
  claimed: boolean;
  reward: {
    amount: number;
    currencyType: 'regular' | 'premium';
  };
}

export interface GameVictory {
  id: number;
  gameId: number;
  gameName: string;
  date: string;
  wonOrLost: string;
  reward: {
    amount: number;
    currencyType: 'regular' | 'premium';
  };
  claimed: boolean;
}

interface RewardsComponentProps {
  limit?: number;
}

export function AchievementRewards({ limit = 15 }: RewardsComponentProps) {
  const { toast } = useToast();
  const [displayLimit, setDisplayLimit] = useState<number>(limit);
  const [incompleteDisplayLimit, setIncompleteDisplayLimit] = useState<number>(3);
  const [claimedDisplayLimit, setClaimedDisplayLimit] = useState<number>(5);
  const { data: achievements, isLoading, isError, refetch } = useAchievementRewards(displayLimit);
  const { mutate: claimReward, isPending } = useClaimAchievementReward();

  // Process achievements data
  const unclaimedAchievements = React.useMemo(() => {
    if (!Array.isArray(achievements)) return [];
    return achievements.filter(a => a.completed && !a.claimed);
  }, [achievements]);
  
  const claimedAchievements = React.useMemo(() => {
    if (!Array.isArray(achievements)) return [];
    return achievements.filter(a => a.completed && a.claimed);
  }, [achievements]);
  
  const incompleteAchievements = React.useMemo(() => {
    if (!Array.isArray(achievements)) return [];
    return achievements.filter(a => !a.completed);
  }, [achievements]);

  // Effect to load more when there are no unclaimed achievements
  useEffect(() => {
    if (Array.isArray(achievements) && 
        unclaimedAchievements.length === 0 && 
        achievements.length >= displayLimit) {
      setDisplayLimit(prev => prev + 10);
    }
  }, [achievements, unclaimedAchievements.length, displayLimit]);

  const handleClaimReward = (achievementId: number) => {
    claimReward(achievementId, {
      onSuccess: (data: any) => {
        // Play achievement reward sound effect
        appSound.playAchievementRewardSound();
        
        if (data && data.reward) {
          toast({
            title: "Reward claimed!",
            description: `You received ${data.reward.amount} ${data.reward.currencyType === 'premium' ? 'gems' : 'coins'}.`,
            variant: "default",
          });
        } else {
          toast({
            title: "Reward claimed!",
            description: "Your reward has been added to your account.",
            variant: "default",
          });
        }
        refetch();
      },
      onError: (error: any) => {
        toast({
          title: "Failed to claim reward",
          description: "There was an error claiming your reward.",
          variant: "destructive",
        });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="bg-gray-950 border-gray-800 shadow-md">
            <CardHeader>
              <Skeleton className="h-5 w-3/4 bg-gray-800" />
              <Skeleton className="h-4 w-1/2 bg-gray-800" />
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center">
                <Skeleton className="h-10 w-24 bg-gray-800" />
                <Skeleton className="h-10 w-24 bg-gray-800" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (isError || !achievements) {
    return (
      <div className="rounded-md bg-red-950 border border-red-800 p-4 text-red-300 text-sm flex items-center gap-2 shadow-md">
        <AlertCircle className="h-4 w-4" />
        <span>Failed to load achievement rewards</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {unclaimedAchievements.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-md font-semibold mb-2 flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-500" />
            Ready to Claim
          </h3>
          <div className="space-y-3">
            {unclaimedAchievements.map((achievement: Achievement) => (
              <Card key={achievement.id} className="bg-gray-950 border-gray-800 text-white shadow-md">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-sm font-medium text-white">{achievement.name}</CardTitle>
                    <div className="flex items-center text-sm">
                      {achievement.reward.currencyType === 'premium' ? (
                        <Gem className="h-4 w-4 text-purple-500 mr-1" />
                      ) : (
                        <Coins className="h-4 w-4 text-amber-500 mr-1" />
                      )}
                      <span className="text-amber-400">{achievement.reward.amount}</span>
                    </div>
                  </div>
                  <CardDescription className="text-xs text-gray-400">{achievement.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <Button 
                    size="sm" 
                    className="w-full bg-primary hover:bg-primary/90 text-black font-medium shadow-md" 
                    disabled={isPending}
                    onClick={() => handleClaimReward(achievement.id)}
                  >
                    {isPending ? 'Claiming...' : 'Claim Reward'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* In progress achievements */}
      {incompleteAchievements.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-md font-semibold mb-2 flex items-center gap-2">
            <Zap className="h-4 w-4 text-blue-500" />
            In Progress
          </h3>
          <div className="space-y-3">
            {incompleteAchievements.slice(0, incompleteDisplayLimit).map((achievement: Achievement) => (
              <Card key={achievement.id} className="bg-gray-950 border-gray-800 text-white shadow-md">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-sm font-medium text-white">{achievement.name}</CardTitle>
                    <div className="flex items-center text-sm">
                      {achievement.reward.currencyType === 'premium' ? (
                        <Gem className="h-4 w-4 text-purple-500 mr-1" />
                      ) : (
                        <Coins className="h-4 w-4 text-amber-500 mr-1" />
                      )}
                      <span className="text-amber-400">{achievement.reward.amount}</span>
                    </div>
                  </div>
                  <CardDescription className="text-xs text-gray-400">{achievement.description}</CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <div className="w-full h-2 bg-gray-800 rounded-full mt-1">
                    <div 
                      className="h-full bg-blue-500 rounded-full" 
                      style={{ width: `${(achievement.progress / achievement.requirement) * 100}%` }}
                    />
                  </div>
                  <div className="flex justify-between mt-1 text-xs text-gray-400">
                    <span>Progress</span>
                    <span>{achievement.progress} / {achievement.requirement}</span>
                  </div>
                </CardContent>
              </Card>
            ))}
            
            {/* Show "View More" button for incomplete achievements */}
            {incompleteAchievements.length > incompleteDisplayLimit && (
              <Button 
                variant="ghost" 
                size="sm"
                className="w-full text-gray-500 hover:text-gray-400"
                onClick={() => setIncompleteDisplayLimit(prev => prev + 5)}
              >
                View More In Progress
              </Button>
            )}
          </div>
        </div>
      )}

      {/* Recently claimed achievements */}
      {claimedAchievements.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-md font-semibold mb-2 flex items-center gap-2">
            <Medal className="h-4 w-4 text-green-500" />
            Recently Claimed
          </h3>
          <div className="space-y-3">
            {claimedAchievements.slice(0, claimedDisplayLimit).map((achievement: Achievement) => (
              <Card key={achievement.id} className="bg-gray-900 border-gray-800 text-white opacity-70 shadow-sm">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-sm font-medium text-white">{achievement.name}</CardTitle>
                    <div className="flex items-center text-sm">
                      {achievement.reward.currencyType === 'premium' ? (
                        <Gem className="h-4 w-4 text-purple-500 mr-1" />
                      ) : (
                        <Coins className="h-4 w-4 text-amber-500 mr-1" />
                      )}
                      <span className="text-amber-400">{achievement.reward.amount}</span>
                    </div>
                  </div>
                  <CardDescription className="text-xs text-gray-400">{achievement.description}</CardDescription>
                </CardHeader>
              </Card>
            ))}
            
            {/* Show "View More" button for claimed achievements */}
            {claimedAchievements.length > claimedDisplayLimit && (
              <Button 
                variant="ghost" 
                size="sm"
                className="w-full text-gray-500 hover:text-gray-400"
                onClick={() => setClaimedDisplayLimit(prev => prev + 5)}
              >
                View More Claimed Rewards
              </Button>
            )}
          </div>
        </div>
      )}
      
      {/* Load more button - only show if there are more items to load from API */}
      {Array.isArray(achievements) && achievements.length >= displayLimit && (
        <div className="flex justify-center mt-4">
          <Button 
            variant="outline" 
            size="sm"
            className="bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
            onClick={() => setDisplayLimit(prev => prev + 10)}
          >
            Load More Rewards
          </Button>
        </div>
      )}
    </div>
  );
}

export function GameVictoryRewards({ limit = 15 }: RewardsComponentProps) {
  const { toast } = useToast();
  const [displayLimit, setDisplayLimit] = useState<number>(limit);
  const [claimedDisplayLimit, setClaimedDisplayLimit] = useState<number>(10); 
  const { data: gameVictories, isLoading, isError, refetch } = useGameVictoryRewards(displayLimit);
  const { mutate: claimReward, isPending } = useClaimGameReward();
  
  // Process game victories data
  const unclaimedVictories = React.useMemo(() => {
    if (!Array.isArray(gameVictories)) return [];
    return gameVictories.filter(g => !g.claimed);
  }, [gameVictories]);
  
  const claimedVictories = React.useMemo(() => {
    if (!Array.isArray(gameVictories)) return [];
    return gameVictories.filter(g => g.claimed);
  }, [gameVictories]);

  // Always include useEffect hook regardless of state
  useEffect(() => {
    // Only execute the logic if we have the data
    if (Array.isArray(gameVictories) && 
        unclaimedVictories.length === 0 && 
        claimedVictories.length >= displayLimit) {
      setDisplayLimit(prev => prev + 10);
    }
  }, [gameVictories, unclaimedVictories.length, claimedVictories.length, displayLimit]);

  const handleClaimReward = (gameId: number) => {
    claimReward(gameId, {
      onSuccess: (data: any) => {
        // Play game victory reward sound effect
        appSound.playGameVictoryRewardSound();
        
        if (data && data.reward) {
          toast({
            title: "Reward claimed!",
            description: `You received ${data.reward.amount} ${data.reward.currencyType === 'premium' ? 'gems' : 'coins'}.`,
            variant: "default",
          });
        } else {
          toast({
            title: "Reward claimed!",
            description: "Your reward has been added to your account.",
            variant: "default",
          });
        }
        refetch();
      },
      onError: (error: any) => {
        toast({
          title: "Failed to claim reward",
          description: "There was an error claiming your reward.",
          variant: "destructive",
        });
      }
    });
  };

  if (isLoading) {
    return (
      <div className="space-y-4">
        {[1, 2, 3].map((i) => (
          <Card key={i} className="bg-gray-950 border-gray-800 shadow-md">
            <CardHeader>
              <Skeleton className="h-5 w-3/4 bg-gray-800" />
              <Skeleton className="h-4 w-1/2 bg-gray-800" />
            </CardHeader>
            <CardContent>
              <div className="flex justify-between items-center">
                <Skeleton className="h-10 w-24 bg-gray-800" />
                <Skeleton className="h-10 w-24 bg-gray-800" />
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    );
  }

  if (isError || !gameVictories) {
    return (
      <div className="rounded-md bg-red-950 border border-red-800 p-4 text-red-300 text-sm flex items-center gap-2 shadow-md">
        <AlertCircle className="h-4 w-4" />
        <span>Failed to load game rewards</span>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      {unclaimedVictories.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-md font-semibold mb-2 flex items-center gap-2">
            <Trophy className="h-4 w-4 text-amber-500" />
            Ready to Claim
          </h3>
          <div className="space-y-3">
            {unclaimedVictories.map((victory: GameVictory) => (
              <Card key={victory.id} className="bg-gray-950 border-gray-800 text-white shadow-md">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-sm font-medium text-white">{victory.gameName}</CardTitle>
                    <div className="flex items-center text-sm">
                      {victory.reward.currencyType === 'premium' ? (
                        <Gem className="h-4 w-4 text-purple-500 mr-1" />
                      ) : (
                        <Coins className="h-4 w-4 text-amber-500 mr-1" />
                      )}
                      <span className="text-amber-400">{victory.reward.amount}</span>
                    </div>
                  </div>
                  <CardDescription className="text-xs text-gray-400">
                    {victory.wonOrLost} {formatDistanceToNow(new Date(victory.date), { addSuffix: true })}
                  </CardDescription>
                </CardHeader>
                <CardContent className="pt-0">
                  <Button 
                    size="sm" 
                    className="w-full bg-primary hover:bg-primary/90 text-black font-medium shadow-md" 
                    disabled={isPending}
                    onClick={() => handleClaimReward(victory.gameId)}
                  >
                    {isPending ? 'Claiming...' : 'Claim Reward'}
                  </Button>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}

      {/* Recently claimed victories */}
      {claimedVictories.length > 0 && (
        <div className="space-y-2">
          <h3 className="text-md font-semibold mb-2 flex items-center gap-2">
            <Medal className="h-4 w-4 text-green-500" />
            Recently Claimed
          </h3>
          <div className="space-y-3">
            {claimedVictories.slice(0, claimedDisplayLimit).map((victory: GameVictory) => (
              <Card key={victory.id} className="bg-gray-900 border-gray-800 text-white opacity-70 shadow-sm">
                <CardHeader className="pb-2">
                  <div className="flex justify-between items-start">
                    <CardTitle className="text-sm font-medium text-white">{victory.gameName}</CardTitle>
                    <div className="flex items-center text-sm">
                      {victory.reward.currencyType === 'premium' ? (
                        <Gem className="h-4 w-4 text-purple-500 mr-1" />
                      ) : (
                        <Coins className="h-4 w-4 text-amber-500 mr-1" />
                      )}
                      <span className="text-amber-400">{victory.reward.amount}</span>
                    </div>
                  </div>
                  <CardDescription className="text-xs text-gray-400">
                    Won {formatDistanceToNow(new Date(victory.date), { addSuffix: true })}
                  </CardDescription>
                </CardHeader>
              </Card>
            ))}
            
            {/* Show "View More" button if there are more claimed victories than displayed */}
            {claimedVictories.length > claimedDisplayLimit && (
              <Button 
                variant="ghost" 
                size="sm"
                className="w-full text-gray-500 hover:text-gray-400"
                onClick={() => setClaimedDisplayLimit(prev => prev + 5)}
              >
                View More Claimed Rewards
              </Button>
            )}
          </div>
        </div>
      )}

      {unclaimedVictories.length === 0 && claimedVictories.length === 0 && (
        <div className="text-center p-6 bg-gray-950 border border-gray-800 rounded-lg shadow-md text-gray-400">
          <Trophy className="h-12 w-12 mx-auto mb-3 text-gray-600" />
          <p>You haven't won/lost any games recently</p>
          <p className="text-sm mt-2 text-gray-500">Play games and win to earn rewards!</p>
        </div>
      )}
      
      {/* Load More button - only show if we might have more rewards to load from API */}
      {Array.isArray(gameVictories) && gameVictories.length >= displayLimit && (
        <div className="flex justify-center mt-4">
          <Button 
            variant="outline" 
            size="sm"
            className="bg-gray-900 border-gray-700 text-gray-300 hover:bg-gray-800 hover:text-white"
            onClick={() => setDisplayLimit(prev => prev + 10)}
          >
            Load More Rewards
          </Button>
        </div>
      )}
    </div>
  );
}

// Component that imports ClaimAchievementReward to avoid circular imports
function useClaimAchievementReward() {
  return useAchievementRewards().claimAchievementReward;
}

// Component that imports ClaimGameReward to avoid circular imports
function useClaimGameReward() {
  return useGameVictoryRewards().claimGameReward;
}